/**
 * Балльная оценка GO / COND / NO-GO (ТЗ 11.11, WS 15587410 п.3).
 *
 * Перенос 1:1 из Launch Pipeline (`frontend/src/shared/lib/calculations/goNogo.ts`, балльное
 * ядро PLP-23; бэкенд-зеркало там же — `backend/app/core/calculations/go_nogo.py`). Порядок:
 * автоотказы (профит < $0 / ROI < 20% / маржа < 15%) → NO_GO; иначе сумма баллов по доступным
 * критериям × множители. Ниша привязана — группы А/Б/В по сумме; не привязана — предварительный
 * вердикт по доле набранных баллов (флаг isPartial). Нет ни одной метрики — «нет данных».
 */

import {
  AUTO_REJECT_MIN_MARGIN_PERCENT,
  AUTO_REJECT_MIN_PROFIT_USD,
  AUTO_REJECT_MIN_ROI_PERCENT,
  BASE_CRITERION_POINTS,
  DIFFERENTIATION_SCORE,
  FRAGILITY_SCORE,
  MARGIN_SCORE_T1_PCT,
  MARGIN_SCORE_T2_PCT,
  MULTIPLIER_DIFFERENTIATION,
  MULTIPLIER_FRAGILITY,
  MULTIPLIER_MARGIN,
  MULTIPLIER_PROFIT,
  MULTIPLIER_ROI,
  MULTIPLIER_WEIGHT,
  POINTS_GROUP_THRESHOLD_A,
  POINTS_GROUP_THRESHOLD_B,
  PROFIT_SCORE_T1_USD,
  PROFIT_SCORE_T2_USD,
  PROFIT_SCORE_T3_USD,
  PROFIT_SCORE_T4_USD,
  ROI_SCORE_T1_PCT,
  ROI_SCORE_T2_PCT,
  ROI_SCORE_T3_PCT,
  ROI_SCORE_T4_PCT,
  SCORE_RATIO_CONDITIONAL,
  SCORE_RATIO_GO,
  TOTAL_SCORING_CRITERIA,
  WEIGHT_SCORE_T1_KG,
  WEIGHT_SCORE_T2_KG,
  WEIGHT_SCORE_T3_KG,
  WEIGHT_SCORE_T4_KG,
  type AutoRejectReason,
  type DifferentiationLevel,
  type FragilityLevel,
  type Recommendation,
  type ScoreGroup,
  type ScoringBlock,
} from './constants';

function perUnitProfitScore(profit: number): number {
  if (profit >= PROFIT_SCORE_T4_USD) return 1.0;
  if (profit >= PROFIT_SCORE_T3_USD) return 0.75;
  if (profit >= PROFIT_SCORE_T2_USD) return 0.5;
  if (profit >= PROFIT_SCORE_T1_USD) return 0.25;
  return 0.0;
}

function roiScore(roi: number): number {
  if (roi >= ROI_SCORE_T4_PCT) return 1.0;
  if (roi >= ROI_SCORE_T3_PCT) return 0.75;
  if (roi >= ROI_SCORE_T2_PCT) return 0.5;
  if (roi >= ROI_SCORE_T1_PCT) return 0.25;
  return 0.0;
}

function marginScore(margin: number): number {
  if (margin >= MARGIN_SCORE_T2_PCT) return 1.0;
  if (margin >= MARGIN_SCORE_T1_PCT) return 0.5;
  return 0.0;
}

function weightScore(weightKg: number): number {
  if (weightKg <= WEIGHT_SCORE_T4_KG) return 1.0;
  if (weightKg <= WEIGHT_SCORE_T3_KG) return 0.75;
  if (weightKg <= WEIGHT_SCORE_T2_KG) return 0.5;
  if (weightKg <= WEIGHT_SCORE_T1_KG) return 0.25;
  return 0.0;
}

/**
 * Идентификатор niche-критерия (PLP-23). Подмножество `ScoringCriterionId`; порядок и состав
 * зафиксированы в `nicheCriteria.ts` и совпадают с бэком.
 */
export type NicheCriterionId =
  | 'nicheDemandVolume'
  | 'nicheRealDemand'
  | 'nicheConversion'
  | 'nicheDemandGrowth90'
  | 'nicheYoyTrend'
  | 'nicheActualSales'
  | 'nichePriceLevel'
  | 'nicheSaturation'
  | 'nicheTop5Concentration'
  | 'nicheReviewsStrength'
  | 'nicheCompetitorRating'
  | 'nicheReturnRate';

/**
 * Идентификатор критерия балльной оценки. Одновременно суффикс ключа словаря
 * (`card.scoring.criterion.<id>`), поэтому это объединение литералов, а не `string`: критерий
 * без перевода перестаёт компилироваться, а не показывает сырой ключ в таблице (CR-27).
 */
export type ScoringCriterionId =
  | 'unitProfit'
  | 'roi'
  | 'margin'
  | 'weight'
  | 'fragility'
  | 'differentiation'
  | NicheCriterionId;

/** Результат одного niche-критерия (Спрос/Конкуренция) — из niche-данных платформы
 *  (PLP-24 + маппинг Прил. А, п.4 — backend-gap). */
export interface NicheCriterionInput {
  id: NicheCriterionId;
  block: 'demand' | 'competition';
  rawScore: number; // 0..1
  multiplier: number; // 0.5..2.0
}

export interface GoNoGoScoreInput {
  profitPerUnit: number | null;
  roi: number | null;
  margin: number | null;
  weightKg?: number | null;
  fragility?: FragilityLevel | null;
  differentiation?: DifferentiationLevel | null;
  /** Отсутствие/пустой массив = ниша не привязана → частичная оценка E9. */
  nicheCriteria?: readonly NicheCriterionInput[] | null;
}

export interface CriterionScore {
  id: ScoringCriterionId;
  block: ScoringBlock;
  rawScore: number | null;
  multiplier: number;
  points: number;
  counted: boolean;
}

export interface GoNoGoScore {
  verdict: Recommendation | 'PENDING';
  hasData: boolean;
  autoReject: AutoRejectReason | null;
  isPartial: boolean;
  partialReason: 'NICHE_NOT_BOUND' | null;
  totalPoints: number;
  maxPoints: number;
  percentCriteria: number;
  group: ScoreGroup | null;
  countedCriteria: number;
  totalCriteria: number;
  breakdown: CriterionScore[];
}

/**
 * Автоотказ по юнит-экономике (ТЗ 11.11 + 1Б.2.3): профит < $0 / ROI < 20% / маржа < 15%.
 * Экспортируется, потому что отсев автоотказов нужен не только вердикту: ранжирование идей по
 * потенциалу тоже отсекает их ДО сортировки (WS 15471018), и второй набор порогов рядом с этим
 * означал бы, что таблица и воронка однажды разойдутся с вердиктом карточки.
 * `null` во входе — «нет данных»: критерий не проверяется, а не считается проваленным.
 */
export function resolveAutoReject(
  profit: number | null,
  roi: number | null,
  margin: number | null,
): AutoRejectReason | null {
  if (profit !== null && profit < AUTO_REJECT_MIN_PROFIT_USD) return 'PROFIT_NEGATIVE';
  if (roi !== null && roi < AUTO_REJECT_MIN_ROI_PERCENT) return 'ROI_BELOW_MIN';
  if (margin !== null && margin < AUTO_REJECT_MIN_MARGIN_PERCENT) return 'MARGIN_BELOW_MIN';
  return null;
}

/**
 * Балльная оценка GO/COND/NO-GO по мануалу (ТЗ 11.11) — каркас PLP-23.
 * Порядок: автоотказы (профит<$0 / ROI<20% / маржа<15%) → NO_GO; иначе сумма
 * баллов по доступным критериям. Полный путь (ниша привязана) — группы А/Б/В;
 * частичная оценка E9 (ниша не привязана) — предварительный вердикт по
 * «% критериев» с флагом isPartial. hasData=false — «нет данных» (E1).
 */
export function evaluateGoNoGoScore(input: GoNoGoScoreInput): GoNoGoScore {
  const profit = input.profitPerUnit;
  const roi = input.roi;
  const margin = input.margin;
  const hasData = profit !== null || roi !== null || margin !== null;
  const autoReject = resolveAutoReject(profit, roi, margin);

  const breakdown: CriterionScore[] = [];
  const add = (id: ScoringCriterionId, block: ScoringBlock, raw: number | null, multiplier: number): void => {
    if (raw === null) {
      breakdown.push({ id, block, rawScore: null, multiplier, points: 0, counted: false });
      return;
    }
    breakdown.push({
      id,
      block,
      rawScore: raw,
      multiplier,
      points: raw * multiplier * BASE_CRITERION_POINTS,
      counted: true,
    });
  };

  add('unitProfit', 'unitEconomics', profit !== null ? perUnitProfitScore(profit) : null, MULTIPLIER_PROFIT);
  add('roi', 'unitEconomics', roi !== null ? roiScore(roi) : null, MULTIPLIER_ROI);
  add('margin', 'unitEconomics', margin !== null ? marginScore(margin) : null, MULTIPLIER_MARGIN);
  add(
    'weight',
    'logistics',
    input.weightKg !== null && input.weightKg !== undefined ? weightScore(input.weightKg) : null,
    MULTIPLIER_WEIGHT,
  );
  add(
    'fragility',
    'logistics',
    input.fragility ? FRAGILITY_SCORE[input.fragility] : null,
    MULTIPLIER_FRAGILITY,
  );
  add(
    'differentiation',
    'logistics',
    input.differentiation ? DIFFERENTIATION_SCORE[input.differentiation] : null,
    MULTIPLIER_DIFFERENTIATION,
  );

  const nichePresent = !!input.nicheCriteria && input.nicheCriteria.length > 0;
  if (input.nicheCriteria) {
    for (const nc of input.nicheCriteria) add(nc.id, nc.block, nc.rawScore, nc.multiplier);
  }
  const isPartial = !nichePresent;

  const counted = breakdown.filter((b) => b.counted);
  const totalPoints = counted.reduce((sum, b) => sum + b.points, 0);
  const maxPoints = counted.reduce((sum, b) => sum + BASE_CRITERION_POINTS * b.multiplier, 0);
  const ratio = maxPoints > 0 ? totalPoints / maxPoints : 0;

  let group: ScoreGroup | null = null;
  if (hasData && autoReject === null) {
    group =
      totalPoints >= POINTS_GROUP_THRESHOLD_A
        ? 'A'
        : totalPoints >= POINTS_GROUP_THRESHOLD_B
          ? 'B'
          : 'C';
  }

  let verdict: Recommendation | 'PENDING';
  if (!hasData) {
    verdict = 'PENDING';
  } else if (autoReject !== null) {
    verdict = 'NO_GO';
  } else if (isPartial) {
    verdict = ratio >= SCORE_RATIO_GO ? 'GO' : ratio >= SCORE_RATIO_CONDITIONAL ? 'CONDITIONAL' : 'NO_GO';
  } else {
    verdict = group === 'A' ? 'GO' : group === 'B' ? 'CONDITIONAL' : 'NO_GO';
  }

  return {
    verdict,
    hasData,
    autoReject,
    isPartial,
    partialReason: isPartial ? 'NICHE_NOT_BOUND' : null,
    totalPoints: Math.round(totalPoints),
    maxPoints: Math.round(maxPoints),
    percentCriteria: Math.round(ratio * 100),
    group,
    countedCriteria: counted.length,
    totalCriteria: TOTAL_SCORING_CRITERIA,
    breakdown,
  };
}

/**
 * Преобладающий вердикт по не-отклонённым вариантам линейки/бренда (ТЗ 1А.4).
 * Плюралитет; тай-брейк консервативный (NO_GO ≻ CONDITIONAL ≻ GO).
 * null — нет не-отклонённых вариантов (нет данных).
 */
export function majorityVerdict(
  go: number,
  conditional: number,
  noGo: number,
): Recommendation | null {
  if (go + conditional + noGo <= 0) return null;
  if (noGo >= go && noGo >= conditional) return 'NO_GO';
  if (conditional >= go) return 'CONDITIONAL';
  return 'GO';
}
